import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Box, CircularProgress, Typography, styled } from "@mui/material";
import { useEffect } from "react";
import { getCoinChannelWise } from "../Services/apiRoutes";
import { SocialIcon } from "react-social-icons";
import { Icon } from "./Icon";
import { MarketRate } from "./MarketRate";
import { getCryptoLogoMap } from "../utils/utility";
import PriceSentimentDialog from './PriceSentimentDialog'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#D5B458",
    color: theme.palette.common.black,
    padding: "10px 20px",
    borderRight: "1px solid #FFFFFF",
    fontFamily: "Inter",
    marginTop: "2px",
    fontWeight: "700",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

function formatNumbers(number, decimalPoint = 2) {
  if (!isNaN(number)) {
    return Number(number)?.toFixed(decimalPoint);
  } else {
    return number;
  }
}

function createData(index, coin, channel, sentiment, pr, day, month, year) {
  let price = formatNumbers(pr, 4);
  let d = formatNumbers(day);
  let m = formatNumbers(month);
  let y = formatNumbers(year);
  return { index, coin, channel, sentiment, price, d, m, y };
}

let coinSymbol = [];

export default function DenseTable({ positive }) {
  const [rows, setRows] = React.useState([]);
  const [iconURL, setIconURL] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [priceSentimentsChartOpen, setPriceSentimentsChartOpen] = React.useState(false);
  const [priceSentimentsChartCoin, setPriceSentimentsChartCoin] = React.useState();

  useEffect(() => {
    fetchCoinChannelData();
  }, []);

  const fetchCoinChannelData = async () => {
    try {
      const data = await getCoinChannelWise();
      let dataType = positive ? data.slice(0, 10) : data.slice(-10).reverse();
      let formattedData = dataType.map((element, index) => {
        return createData(index, element.coins, element.channel, element.weighted_average.toFixed(2), element.current_price, element["1d"], element["1m"], element["1y"]);
      });
      // console.log(data);
      pushCoins(dataType);
      getResult();
      setRows(formattedData);
    } catch (error) {
      console.log("error ocuured", error);
    } finally {
      setLoading(false);
    }
  };
  const getResult = async () => {
    var result = await getCryptoLogoMap(coinSymbol);
    setIconURL(result);
    return result;
  };

  const pushCoins = (dataArray) => {
    dataArray.forEach((data) => {
      coinSymbol.push(data.coins);
    });
  };

  const handlePriceChartClose = () => {
    setPriceSentimentsChartOpen(false);
  };

  const handleCoinClick = (coin) => {
    setPriceSentimentsChartCoin(coin.trim().toUpperCase());
    setPriceSentimentsChartOpen(true);
  };

  return (
    <>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "50px",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <div>
          {priceSentimentsChartOpen && <PriceSentimentDialog open={priceSentimentsChartOpen} handleClose={handlePriceChartClose} coin={priceSentimentsChartCoin} />}

          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
              <TableHead
                style={{
                  backgroundColor: "black",
                  color: "white",
                }}
              >
                <TableRow>
                  <StyledTableCell>#</StyledTableCell>
                  <StyledTableCell>Coin</StyledTableCell>
                  <StyledTableCell align="center">Channel</StyledTableCell>
                  <StyledTableCell align="center">Auro Sentiment</StyledTableCell>
                  <StyledTableCell align="center">Current price</StyledTableCell>

                  <StyledTableCell align="center" colSpan={3}>
                    <div>(Price change) </div>
                    <div
                      style={{
                        color: "white",
                        display: "flex",
                        gap: "20px",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>1d</span>
                      <span>1m</span>
                      <span>1y</span>
                    </div>
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <StyledTableRow  
                  onClick={()=>{handleCoinClick(row.coin)}}
                  key={row.name}>
                    <StyledTableCell component="th" scope="row">
                      {row.index + 1}
                    </StyledTableCell>
                    <StyledTableCell component="th" scope="row">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box>
                          <Icon name={row.coin?.toLowerCase()} url={iconURL[row.coin.toUpperCase()]} />
                        </Box>
                        <Box>
                          <Typography fontWeight="600" fontSize={12}>
                            {row.coin}
                          </Typography>
                        </Box>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {
                        <SocialIcon
                          network={row.channel?.toLowerCase()}
                          style={{
                            width: "25px",
                            height: "25px",
                          }}
                        />
                      }
                    </StyledTableCell>
                    <StyledTableCell align="center">{<MarketRate value={row.sentiment} />}</StyledTableCell>
                    <StyledTableCell align="center">{<MarketRate type="currency" value={row.price} />}</StyledTableCell>
                    <StyledTableCell align="center">{<MarketRate type="percentage" value={row.d} />}</StyledTableCell>
                    <StyledTableCell align="center">{<MarketRate type="percentage" value={row.m} />}</StyledTableCell>

                    <StyledTableCell align="center">{<MarketRate type="percentage" value={row.y} />}</StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
    </>
  );
}
